import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FloppyDisk, Eye, Database, Trash, ArrowsClockwise, Stack, Tag, Question } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
    buildTemplateSectionsFromPrototype,
    diffTemplateAgainstPrototype,
    applyTemplateToPrototype,
} from "../builderTemplateNormalizer";

/**
 * Iter 97.19 — Section 5: Backend-persisted Builder Templates.
 *
 * Save dialog: operator picks which sections to capture (Scope · Commercials
 * · AMC · Tax Display · T&C). Apply picker lists ACTIVE templates; clicking
 * one opens a diff dialog showing exactly what will change in the current
 * Builder. Confirm = apply + auto-bump usage counter on the backend.
 *
 * Permissions:
 *   - List + Apply: `sales:read` (any commercial user)
 *   - Create/Edit/Delete: `template:write` (governed by RBAC)
 */
export default function BuilderTemplatesSection({ prototype, proposal, onApplyToBuilder }) {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [showArchived, setShowArchived] = useState(false);

    const [saveOpen, setSaveOpen] = useState(false);
    const [applyOpen, setApplyOpen] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const loadTemplates = async () => {
        try {
            setLoadingList(true);
            const { data } = await api.get("/billing/builder-templates/list");
            setTemplates(data.items || []);
        } catch (e) {
            toast.error(`Failed to load templates: ${e?.message || "Unknown"}`);
        } finally {
            setLoadingList(false);
        }
    };
    useEffect(() => { loadTemplates(); }, []);

    const visibleTemplates = useMemo(() => {
        if (showArchived) return templates;
        return templates.filter((t) => t.status !== "archived");
    }, [templates, showArchived]);

    const archivedCount = useMemo(
        () => templates.filter((t) => t.status === "archived").length,
        [templates],
    );

    const handleOpenPreview = () => {
        const pid = proposal?.id || prototype?.id?.replace(/^proposal-/, "");
        if (!pid) {
            toast.error("Preview unavailable — no proposal context.");
            return;
        }
        navigate(`/proposals/${pid}/builder/preview`);
    };

    return (
        <Card className="border-slate-200" data-testid="section-builder-templates">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <CardTitle className="flex items-center gap-2"><Stack weight="duotone" /> Section 5 — Templates & Preview</CardTitle>
                        <CardDescription>
                            Save this Builder configuration as a reusable template (operator picks which sections to capture). Apply a saved template into this Builder — diff shown before changes land.
                        </CardDescription>
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 gap-1.5 text-[11px] h-7"
                        onClick={loadTemplates}
                        data-testid="builder-templates-refresh-btn"
                        title="Refetch templates"
                    >
                        <ArrowsClockwise size={12} weight="bold" /> Refresh
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Primary actions */}
                <div className="grid gap-2 md:grid-cols-3">
                    <Button onClick={() => setSaveOpen(true)} data-testid="save-as-template-btn" className="gap-1.5">
                        <FloppyDisk size={14} weight="fill" /> Save as Template…
                    </Button>
                    <Button onClick={() => setApplyOpen(true)} variant="outline" data-testid="apply-template-btn" className="gap-1.5">
                        <Stack size={14} weight="bold" /> Apply Template…
                    </Button>
                    <Button onClick={handleOpenPreview} variant="outline" data-testid="open-preview-btn" className="gap-1.5">
                        <Eye size={14} weight="bold" /> Open Print Preview
                    </Button>
                </div>

                {/* Saved Templates list */}
                <div className="border-t border-slate-200 pt-4">
                    <div className="flex items-center justify-between">
                        <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                            <Database weight="duotone" /> Saved Templates ({visibleTemplates.length})
                        </Label>
                        {archivedCount > 0 && (
                            <button
                                type="button"
                                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                                    showArchived
                                        ? "bg-violet-600 text-white border-violet-600"
                                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                                }`}
                                onClick={() => setShowArchived((v) => !v)}
                                data-testid="builder-templates-show-archived-toggle"
                            >
                                {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
                            </button>
                        )}
                    </div>
                    {loadingList ? (
                        <div className="mt-2 text-sm text-slate-500 italic">Loading…</div>
                    ) : visibleTemplates.length === 0 ? (
                        <div className="mt-2 text-sm text-slate-500 italic">
                            No templates yet. Click <span className="font-mono bg-slate-100 px-1 rounded">Save as Template</span> to create the first one.
                        </div>
                    ) : (
                        <ul className="mt-2 space-y-1.5" data-testid="builder-templates-list">
                            {visibleTemplates.map((t) => (
                                <TemplateRow
                                    key={t.id}
                                    template={t}
                                    onApply={(tpl) => { setSelectedTemplate(tpl); setDiffOpen(true); }}
                                    onRefresh={loadTemplates}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </CardContent>

            <SaveTemplateDialog
                open={saveOpen}
                onOpenChange={setSaveOpen}
                prototype={prototype}
                onSaved={loadTemplates}
            />

            <ApplyTemplatePickerDialog
                open={applyOpen}
                onOpenChange={setApplyOpen}
                templates={visibleTemplates.filter((t) => t.status === "active")}
                onPick={(tpl) => { setApplyOpen(false); setSelectedTemplate(tpl); setDiffOpen(true); }}
            />

            <ApplyTemplateDiffDialog
                open={diffOpen}
                onOpenChange={(o) => { setDiffOpen(o); if (!o) setSelectedTemplate(null); }}
                template={selectedTemplate}
                prototype={prototype}
                proposal={proposal}
                onConfirm={async (nextPrototype, templateId) => {
                    onApplyToBuilder(nextPrototype);
                    setDiffOpen(false);
                    try {
                        await api.post(`/billing/builder-templates/${templateId}/touch`, {
                            proposal_id: proposal?.id || null,
                        });
                        loadTemplates();
                    } catch {
                        /* non-blocking — apply already landed */
                    }
                    toast.success("Template applied to Builder.");
                }}
            />
        </Card>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   TEMPLATE ROW
   ───────────────────────────────────────────────────────────────────── */

function TemplateRow({ template, onApply, onRefresh }) {
    const handleArchive = async () => {
        const target = template.status === "archived" ? "active" : "archived";
        try {
            await api.patch(`/billing/builder-templates/${template.id}`, { status: target });
            toast.success(`Template ${target === "archived" ? "archived" : "restored"}.`);
            onRefresh();
        } catch (e) {
            toast.error(`Failed: ${e?.message || "Unknown"}`);
        }
    };

    const handleDelete = async () => {
        if (!confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/billing/builder-templates/${template.id}`);
            toast.success("Template deleted.");
            onRefresh();
        } catch (e) {
            toast.error(`Failed: ${e?.message || "Unknown"}`);
        }
    };

    return (
        <li
            className="flex items-start gap-2 p-2.5 rounded border border-slate-200 bg-white"
            data-testid={`template-row-${template.id}`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-semibold truncate">{template.name}</span>
                    {template.version > 1 && (
                        <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">v{template.version}</span>
                    )}
                    {template.status === "archived" && (
                        <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-500 border border-slate-300 px-1.5 py-0.5 rounded-full">Archived</span>
                    )}
                </div>
                {template.description && (
                    <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{template.description}</div>
                )}
                <div className="flex items-center gap-2 flex-wrap mt-1 text-[10px] text-slate-500">
                    <span className="font-mono">{template.code}</span>
                    <span>·</span>
                    <span className="flex items-center gap-0.5">
                        <Tag size={9} weight="bold" />
                        {(template.included_sections || []).join(" · ") || "no sections"}
                    </span>
                    {template.used_count > 0 && (
                        <>
                            <span>·</span>
                            <span title={template.last_used_at ? `Last used ${new Date(template.last_used_at).toLocaleString()}` : ""}>
                                used {template.used_count}×
                            </span>
                        </>
                    )}
                </div>
            </div>
            {template.status !== "archived" && (
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onApply(template)}
                    data-testid={`template-apply-${template.id}`}
                    className="shrink-0"
                >
                    Apply…
                </Button>
            )}
            <Button
                size="sm"
                variant="ghost"
                onClick={handleArchive}
                data-testid={`template-archive-${template.id}`}
                title={template.status === "archived" ? "Restore" : "Archive"}
                className="shrink-0"
            >
                {template.status === "archived" ? "Restore" : "Archive"}
            </Button>
            <Button
                size="sm"
                variant="ghost"
                onClick={handleDelete}
                data-testid={`template-delete-${template.id}`}
                className="shrink-0"
            >
                <Trash size={13} />
            </Button>
        </li>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   SAVE DIALOG
   ───────────────────────────────────────────────────────────────────── */

function SaveTemplateDialog({ open, onOpenChange, prototype, onSaved }) {
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [version, setVersion] = useState(1);
    const [includes, setIncludes] = useState({
        scope: true,
        commercials: true,
        amc: true,
        tax_display: true,
        tnc: true,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setName("");
            setCode("");
            setDescription("");
            setVersion(1);
            setIncludes({ scope: true, commercials: true, amc: true, tax_display: true, tnc: true });
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!name.trim()) { toast.error("Template needs a name."); return; }
        const slug = (code.trim() || name.trim())
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 64);
        if (slug.length < 3) { toast.error("Code must be at least 3 chars."); return; }

        const includedSet = new Set(Object.entries(includes).filter(([, v]) => v).map(([k]) => k));
        if (includedSet.size === 0) { toast.error("Select at least one section."); return; }

        const sections = buildTemplateSectionsFromPrototype(prototype, includedSet);
        const payload = {
            code: slug,
            name: name.trim(),
            description: description.trim(),
            version: Number(version) || 1,
            scope: sections.scope ?? null,
            commercials: sections.commercials ?? null,
            amc: sections.amc ?? null,
            tax_display: sections.tax_display ?? null,
            tnc: sections.tnc ?? null,
        };

        setSubmitting(true);
        try {
            await api.post("/billing/builder-templates", payload);
            toast.success(`Saved template "${payload.name}".`);
            onOpenChange(false);
            onSaved();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Unknown error";
            toast.error(`Save failed: ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="save-template-dialog">
                <DialogHeader>
                    <DialogTitle>Save as Template</DialogTitle>
                    <DialogDescription>
                        Templates are shared across the team. Pick which sections to capture. Section payloads are immutable after save — to update, save a new version.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                            <Label htmlFor="tpl-name">Name *</Label>
                            <Input
                                id="tpl-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Wealth Mgmt Standard"
                                maxLength={160}
                                data-testid="tpl-name-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="tpl-version">Version</Label>
                            <Input
                                id="tpl-version"
                                type="number"
                                value={version}
                                onChange={(e) => setVersion(Number(e.target.value))}
                                min={1}
                                max={999}
                                data-testid="tpl-version-input"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="tpl-code">Code (slug)</Label>
                        <Input
                            id="tpl-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))}
                            placeholder="wealth-mgmt-standard (auto from name if blank)"
                            maxLength={64}
                            className="font-mono"
                            data-testid="tpl-code-input"
                        />
                        <p className="mt-1 text-[10px] text-slate-500">Unique. Auto-generated from name if left blank.</p>
                    </div>
                    <div>
                        <Label htmlFor="tpl-desc">Description (optional)</Label>
                        <Textarea
                            id="tpl-desc"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                            maxLength={600}
                            placeholder="When to use this template; what's included; commercial assumptions"
                            data-testid="tpl-desc-input"
                        />
                    </div>

                    <div className="border-t border-slate-200 pt-3">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Include sections</Label>
                        <div className="mt-2 grid grid-cols-2 gap-2" data-testid="tpl-includes">
                            {[
                                { key: "scope",        label: "Section 1 — Product Scope" },
                                { key: "commercials",  label: "Section 2 — Commercials (pricing + presentation + delivery modes)" },
                                { key: "amc",          label: "Section 2 — AMC pricing" },
                                { key: "tax_display",  label: "Section 3 — Tax Display" },
                                { key: "tnc",          label: "Section 4 — Terms & Conditions" },
                            ].map((s) => (
                                <label key={s.key} className="flex items-start gap-2 text-[12px] text-slate-700 p-2 rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                    <Checkbox
                                        checked={includes[s.key]}
                                        onCheckedChange={(v) => setIncludes((prev) => ({ ...prev, [s.key]: !!v }))}
                                        data-testid={`tpl-include-${s.key}`}
                                    />
                                    <span>{s.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <PlaceholderHint visible={includes.tnc} />
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={submitting} data-testid="tpl-save-confirm-btn" className="gap-1.5">
                        <FloppyDisk size={14} weight="fill" />
                        {submitting ? "Saving…" : "Save Template"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PlaceholderHint({ visible }) {
    const [tokens, setTokens] = useState([]);
    useEffect(() => {
        if (!visible) return;
        api.get("/billing/builder-templates/placeholders/list").then(({ data }) => {
            setTokens(data?.items || []);
        }).catch(() => { /* non-blocking */ });
    }, [visible]);
    if (!visible || tokens.length === 0) return null;
    return (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px]" data-testid="placeholder-hint">
            <div className="font-semibold text-amber-900 flex items-center gap-1">
                <Question size={12} weight="bold" /> T&amp;C — use placeholders, not client-specific text
            </div>
            <div className="mt-1.5 text-amber-900/90">
                Templates are shared. To avoid leaking one client&apos;s data into another&apos;s proposal, use these tokens in T&amp;C text. The Builder resolves them at apply time against the current proposal&apos;s client context.
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
                {tokens.map((t) => (
                    <code key={t.token} className="font-mono text-[10px] bg-white border border-amber-200 px-1.5 py-0.5 rounded" title={t.label}>
                        {t.token}
                    </code>
                ))}
            </div>
        </div>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   APPLY PICKER
   ───────────────────────────────────────────────────────────────────── */

function ApplyTemplatePickerDialog({ open, onOpenChange, templates, onPick }) {
    const [search, setSearch] = useState("");
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter((t) =>
            (t.name || "").toLowerCase().includes(q) ||
            (t.code || "").toLowerCase().includes(q) ||
            (t.description || "").toLowerCase().includes(q),
        );
    }, [templates, search]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="apply-template-picker-dialog">
                <DialogHeader>
                    <DialogTitle>Apply Template</DialogTitle>
                    <DialogDescription>
                        Pick a saved template. The Builder will show a diff dialog before any changes land.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2 space-y-3">
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, code, or description"
                        data-testid="apply-template-search"
                    />
                    {filtered.length === 0 ? (
                        <div className="text-sm text-slate-500 italic py-4 text-center">
                            {templates.length === 0 ? "No templates available." : "No templates match your search."}
                        </div>
                    ) : (
                        <ul className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                            {filtered.map((t) => (
                                <li
                                    key={t.id}
                                    className="flex items-center gap-2 p-2.5 rounded border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer"
                                    onClick={() => onPick(t)}
                                    data-testid={`apply-pick-${t.id}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className="text-sm font-semibold truncate">{t.name}</span>
                                            {t.version > 1 && (
                                                <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">v{t.version}</span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                                            <span className="font-mono">{t.code}</span>
                                            <span>·</span>
                                            <span>{(t.included_sections || []).join(" · ")}</span>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}


/* ─────────────────────────────────────────────────────────────────────
   DIFF DIALOG
   ───────────────────────────────────────────────────────────────────── */

function ApplyTemplateDiffDialog({ open, onOpenChange, template, prototype, proposal, onConfirm }) {
    const diff = useMemo(() => {
        if (!template || !prototype) return null;
        return diffTemplateAgainstPrototype(template, prototype);
    }, [template, prototype]);

    const ctx = useMemo(() => {
        if (!proposal) return {};
        return {
            clientName:    proposal.client_name    || proposal.client?.name || "",
            clientAddress: proposal.client_address || proposal.client?.address_billing || proposal.billing_address || "",
            clientGstin:   proposal.client_gstin   || proposal.client?.gstin || "",
            contactPerson: proposal.contact_person || proposal.client?.contact_person || "",
            proposalDate:  proposal.proposal_date  ? new Date(proposal.proposal_date).toLocaleDateString("en-IN") : "",
            validityDate:  proposal.validity_date  ? new Date(proposal.validity_date).toLocaleDateString("en-IN") : "",
        };
    }, [proposal]);

    if (!template || !diff) return null;

    const handleApply = () => {
        const next = applyTemplateToPrototype(template, prototype, ctx);
        onConfirm(next, template.id);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="apply-template-diff-dialog">
                <DialogHeader>
                    <DialogTitle>Apply Template — Preview Changes</DialogTitle>
                    <DialogDescription>
                        Applying <span className="font-semibold">{template.name}</span>. Review the changes below.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-2 space-y-3 max-h-[420px] overflow-y-auto">
                    {/* Scope */}
                    {template.included_sections?.includes("scope") && (
                        <DiffBlock title="Section 1 — Product Scope">
                            {diff.scope.add.length === 0 && diff.scope.unchanged.length === 0 ? (
                                <i className="text-slate-500">Template has no scope payload.</i>
                            ) : (
                                <>
                                    {diff.scope.add.length > 0 && (
                                        <div data-testid="diff-scope-add">
                                            <div className="text-[11px] font-semibold text-emerald-700">Will be ADDED to current scope:</div>
                                            <ul className="ml-3 mt-0.5 text-[11px] text-slate-700">
                                                {diff.scope.add.map((r, i) => (
                                                    <li key={i}>• {r.productLabel}{r.moduleLabel ? ` → ${r.moduleLabel}` : ""}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {diff.scope.unchanged.length > 0 && (
                                        <div className="mt-1.5">
                                            <div className="text-[11px] font-semibold text-slate-500">Already in scope (kept as-is):</div>
                                            <ul className="ml-3 mt-0.5 text-[11px] text-slate-500">
                                                {diff.scope.unchanged.map((r, i) => (
                                                    <li key={i}>• {r.productLabel}{r.moduleLabel ? ` → ${r.moduleLabel}` : ""}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </DiffBlock>
                    )}
                    {/* Commercials */}
                    {template.included_sections?.includes("commercials") && (
                        <DiffBlock title="Section 2 — Commercials">
                            {diff.commercials.update.length > 0 && (
                                <div data-testid="diff-commercials-update">
                                    <div className="text-[11px] font-semibold text-amber-700">{diff.commercials.update.length} price(s) will be UPDATED:</div>
                                    <ul className="ml-3 mt-0.5 text-[11px] text-slate-700">
                                        {diff.commercials.update.slice(0, 6).map((r, i) => (
                                            <li key={i}>• {r.label} → ₹{Number(r.price).toLocaleString("en-IN")}</li>
                                        ))}
                                        {diff.commercials.update.length > 6 && (
                                            <li className="text-slate-500">…and {diff.commercials.update.length - 6} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                            {diff.commercials.add.length > 0 && (
                                <div className="mt-1.5" data-testid="diff-commercials-add">
                                    <div className="text-[11px] font-semibold text-emerald-700">{diff.commercials.add.length} product(s) NOT in current scope — will be auto-added with prices:</div>
                                    <ul className="ml-3 mt-0.5 text-[11px] text-slate-700">
                                        {diff.commercials.add.slice(0, 6).map((r, i) => (
                                            <li key={i}>• <span className="font-mono">{r.key}</span> → ₹{Number(r.price).toLocaleString("en-IN")}</li>
                                        ))}
                                        {diff.commercials.add.length > 6 && (
                                            <li className="text-slate-500">…and {diff.commercials.add.length - 6} more</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                            <div className="mt-1.5 text-[11px] text-slate-500">
                                Presentation mode will be set to: <span className="font-mono">{template.commercials?.presentationMode || "itemized"}</span>
                            </div>
                        </DiffBlock>
                    )}
                    {/* AMC */}
                    {template.included_sections?.includes("amc") && (diff.amc.update.length + diff.amc.add.length) > 0 && (
                        <DiffBlock title="Section 2 — AMC">
                            <div className="text-[11px] text-slate-700">
                                {diff.amc.update.length} update(s), {diff.amc.add.length} auto-add(s).
                            </div>
                        </DiffBlock>
                    )}
                    {/* Tax Display */}
                    {template.included_sections?.includes("tax_display") && (
                        <DiffBlock title="Section 3 — Tax Display">
                            <div className="text-[11px] text-amber-700 font-semibold" data-testid="diff-tax-replace">
                                Will REPLACE current tax display settings.
                            </div>
                        </DiffBlock>
                    )}
                    {/* T&C */}
                    {template.included_sections?.includes("tnc") && (
                        <DiffBlock title="Section 4 — Terms & Conditions">
                            <div className="text-[11px] text-amber-700 font-semibold" data-testid="diff-tnc-replace">
                                Will REPLACE current T&amp;C. Placeholders ({"{{CLIENT_NAME}} etc."}) will be resolved against this proposal&apos;s client.
                            </div>
                        </DiffBlock>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={handleApply} data-testid="apply-confirm-btn" className="gap-1.5">
                        <FloppyDisk size={14} weight="fill" /> Apply to Builder
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DiffBlock({ title, children }) {
    return (
        <div className="border border-slate-200 rounded-md bg-slate-50 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1.5">{title}</div>
            {children}
        </div>
    );
}
